/**
 * Shared button with named presets for rows, toolbars, forms, modals, and post-its.
 */

import * as React from 'react';
import { Button as ShadcnButton } from '@/components/ui/button';
import {
  LucideIcon,
  Loader2,
  Eye,
  Edit,
  Trash2,
  X,
  CircleX,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Save,
  Plus,
  PlusCircle,
  Upload,
  Check,
  CheckCircle2,
  XCircle,
  Filter,
  SlidersHorizontal,
  ArrowUpDown,
  UserPlus,
  Users,
  Calendar,
  CalendarPlus,
  FileText,
  ClipboardList,
  Archive,
  RotateCcw,
  ArrowLeftRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/** Named visual/behavior presets (row actions, toolbars, modals, pagination). */
type ButtonPreset =
  | 'row-view'
  | 'row-edit'
  | 'row-delete'
  | 'row-archive'
  | 'row-reactivate'
  | 'row-validate'
  | 'row-prevalidate-assignment'
  | 'row-validate-assignment'
  | 'row-cancel'
  | 'row-invite-child'
  | 'row-view-students'
  | 'row-view-sessions'
  | 'row-add-one-off-session'
  | 'row-view-reports'
  | 'row-download'
  | 'section-action'
  | 'download'
  | 'form-close'
  | 'pagination-prev'
  | 'pagination-next'
  | 'pagination-first'
  | 'pagination-last'
  | 'pagination-page'
  | 'form-save'
  | 'form-cancel'
  | 'create-entity'
  | 'import-csv'
  | 'modal-confirm'
  | 'modal-danger'
  | 'modal-cancel'
  | 'modal-edit'
  | 'postit-color'
  | 'postit-action'
  | 'postit-card-action'
  | 'toolbar-filter'
  | 'toolbar-create'
  | 'toolbar-export'
  | 'toolbar-validate'
  | 'toolbar-delete'
  | 'filter-configure'
  | 'toolbar-sort'
  | 'column-pagination-prev'
  | 'column-pagination-next'
  | 'meeting-join'
  | 'meeting-cancel'
  | 'export'
  | 'field-action-add'
  | 'field-action-confirm'
  | 'field-action-cancel'
  | 'filter-yes'
  | 'filter-no'
  | 'contact-swap';

/** Visual defaults applied when a `preset` is set. */
interface PresetConfig {
  variant?: CustomButtonProps['variant'];
  color?: string;
  size?: CustomButtonProps['size'];
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
  tooltipText?: string;
  className?: string;
}

/** Preset → variant / icon / size defaults. */
const BUTTON_PRESETS: Record<ButtonPreset, PresetConfig> = {
  'row-view': {
    variant: 'outline',
    size: 'icon',
    icon: Eye,
    tooltipText: 'Voir les détails',
  },
  'row-edit': {
    variant: 'outline',
    color: 'var(--color-1-nuance-1)',
    size: 'icon',
    icon: Edit,
    tooltipText: 'Modifier',
  },
  'row-delete': {
    variant: 'outline',
    color: 'rgb(var(--color-error))',
    size: 'icon',
    icon: Trash2,
    tooltipText: 'Supprimer',
  },
  'row-archive': {
    variant: 'outline',
    color: 'rgb(var(--color-error))',
    size: 'icon',
    icon: Archive,
    tooltipText: 'Archiver',
  },
  'row-reactivate': {
    variant: 'outline',
    size: 'icon',
    icon: RotateCcw,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Réactiver',
  },
  'row-validate': {
    variant: 'outline',
    size: 'icon',
    icon: CheckCircle2,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Valider',
  },
  'row-prevalidate-assignment': {
    variant: 'outline',
    size: 'icon',
    icon: ClipboardList,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Gérer affectation (prévalidation)',
  },
  'row-validate-assignment': {
    variant: 'outline',
    size: 'icon',
    icon: ClipboardList,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Gérer affectation (validation)',
  },
  'row-cancel': {
    variant: 'outline',
    size: 'icon',
    icon: X,
    color: 'rgb(var(--color-error))',
  },
  'row-invite-child': {
    variant: 'outline',
    size: 'icon',
    icon: UserPlus,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Inviter un enfant',
  },
  'row-view-students': {
    variant: 'outline',
    size: 'icon',
    icon: Users,
    color: 'var(--color-4-nuance-1)',
    tooltipText: 'Voir les élèves',
  },
  'row-view-sessions': {
    variant: 'outline',
    size: 'icon',
    icon: Calendar,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Voir les séances',
  },
  'row-add-one-off-session': {
    variant: 'outline',
    size: 'icon',
    icon: CalendarPlus,
    color: 'var(--color-5-nuance-1)',
    tooltipText: 'Ajouter une séance ponctuelle',
  },
  'row-view-reports': {
    variant: 'outline',
    size: 'icon',
    icon: FileText,
    color: 'var(--color-5-nuance-1)',
    tooltipText: 'Voir les comptes rendus',
  },
  'row-download': {
    variant: 'outline',
    size: 'icon',
    icon: Download,
    color: 'var(--color-2-nuance-1)',
    tooltipText: 'Exporter le bilan',
  },
  'section-action': {
    variant: 'ghost',
    size: 'icon',
    className: 'section-action-button',
  },
  download: {
    variant: 'ghost',
    size: 'icon',
    icon: Download,
    className: 'section-action-button',
    tooltipText: 'Exporter sous format Excel',
  },
  'form-close': {
    variant: 'ghost',
    size: 'icon',
    icon: X,
    tooltipText: 'Fermer',
  },
  'pagination-first': {
    variant: 'ghost',
    size: 'icon',
    icon: ChevronsLeft,
    className: 'table-pagination-button',
    tooltipText: 'Première page',
  },
  'pagination-prev': {
    variant: 'ghost',
    size: 'icon',
    icon: ChevronLeft,
    className: 'table-pagination-button',
    tooltipText: 'Page précédente',
  },
  'pagination-next': {
    variant: 'ghost',
    size: 'icon',
    icon: ChevronRight,
    className: 'table-pagination-button',
    tooltipText: 'Page suivante',
  },
  'pagination-last': {
    variant: 'ghost',
    size: 'icon',
    icon: ChevronsRight,
    className: 'table-pagination-button',
    tooltipText: 'Dernière page',
  },
  'pagination-page': {
    size: 'sm',
    className: 'table-pagination-button',
  },
  'toolbar-filter': {
    variant: 'ghost',
    size: 'icon',
    icon: Filter,
    className: 'section-action-button',
    tooltipText: 'Filtrer',
  },
  'toolbar-create': {
    variant: 'ghost',
    size: 'icon',
    icon: Plus,
    className: 'section-action-button',
  },
  'toolbar-export': {
    variant: 'ghost',
    size: 'icon',
    icon: Download,
    className: 'section-action-button',
    tooltipText: 'Exporter en Excel',
  },
  'toolbar-validate': {
    variant: 'ghost',
    size: 'icon',
    icon: CheckCircle2,
    className: 'section-action-button',
    tooltipText: 'Valider la sélection',
  },
  'toolbar-delete': {
    variant: 'ghost',
    size: 'icon',
    icon: Trash2,
    className: 'section-action-button',
    tooltipText: 'Supprimer la sélection',
  },
  'filter-configure': {
    variant: 'ghost',
    size: 'icon',
    icon: SlidersHorizontal,
    className: 'shrink-0 section-action-button',
    tooltipText: "Configurer l'affichage des filtres",
  },
  'toolbar-sort': {
    variant: 'ghost',
    size: 'icon',
    icon: ArrowUpDown,
    className: 'section-action-button',
  },
  'column-pagination-prev': {
    variant: 'ghost',
    size: 'icon',
    icon: ChevronLeft,
    className: 'section-action-button',
    tooltipText: 'Page précédente',
  },
  'column-pagination-next': {
    variant: 'ghost',
    size: 'icon',
    icon: ChevronRight,
    className: 'section-action-button',
    tooltipText: 'Page suivante',
  },
  'form-save': {
    variant: 'primary',
    size: 'md',
    icon: Save,
    iconPosition: 'left',
    className: 'min-w-40',
  },
  'form-cancel': {
    variant: 'outline',
    size: 'md',
    icon: X,
    iconPosition: 'left',
    className: 'min-w-40',
  },
  'create-entity': {
    variant: 'primary',
    size: 'lg',
    icon: Plus,
    iconPosition: 'left',
    className: 'management-page-create-button',
  },
  'import-csv': {
    variant: 'secondary',
    size: 'lg',
    icon: Upload,
    iconPosition: 'left',
    className: 'management-page-create-button',
  },
  'modal-confirm': {
    variant: 'primary',
    size: 'md',
    icon: Check,
  },
  'modal-danger': {
    variant: 'danger',
    size: 'md',
    icon: Trash2,
  },
  'postit-color': {
    // Circular color swatch; caller sets background + active state via className
    variant: 'custom',
    size: 'sm',
    iconOnly: true,
    className: 'w-7 h-7 rounded-full border-2 transition-all',
  },
  'postit-action': {
    // Icon toggle (priority, status…); caller sets active color via className
    variant: 'custom',
    size: 'icon',
    iconOnly: true,
    className: 'p-1 rounded-lg transition-all hover:scale-105 bg-black/5',
  },
  'postit-card-action': {
    // Icon on the post-it card (transparent bg, adaptive color via className)
    variant: 'custom',
    size: 'icon',
    iconOnly: true,
    className:
      'p-1.5 rounded-md transition-all duration-200 bg-transparent hover:bg-transparent hover:scale-125 hover:drop-shadow-md active:scale-95',
  },
  'modal-cancel': {
    variant: 'outline',
    size: 'md',
    icon: X,
  },
  'modal-edit': {
    variant: 'primary',
    size: 'md',
    icon: Edit,
  },
  'meeting-join': {
    variant: 'outline',
    size: 'icon',
    icon: UserPlus,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Inscrire',
  },
  'meeting-cancel': {
    variant: 'outline',
    size: 'icon',
    icon: X,
    color: 'rgb(var(--color-error))',
    tooltipText: "Annuler l'inscription",
  },
  export: {
    variant: 'secondary',
    size: 'lg',
    icon: Download,
    iconPosition: 'left',
    className: 'management-page-create-button',
  },
  // Inline field action buttons — h-9 w-9 to match modern-input-sm (h-9), outline style, brand/semantic colors.
  // All three use circle icons so visual weight matches across the row.
  'field-action-add': {
    variant: 'outline',
    size: 'md',
    icon: PlusCircle,
    color: 'var(--color-2-nuance-1)',
    tooltipText: 'Ajouter',
  },
  'field-action-confirm': {
    variant: 'outline',
    size: 'md',
    icon: CheckCircle2,
    color: 'var(--color-3-nuance-1)',
    tooltipText: 'Confirmer',
  },
  'field-action-cancel': {
    variant: 'outline',
    size: 'md',
    icon: CircleX,
    color: 'rgb(var(--color-error))',
    tooltipText: 'Annuler',
  },
  'filter-yes': {
    variant: 'custom',
    size: 'sm',
    icon: CheckCircle2,
    iconPosition: 'left',
    color: 'rgb(var(--status-success))',
    className:
      'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
  },
  'filter-no': {
    variant: 'custom',
    size: 'sm',
    icon: XCircle,
    iconPosition: 'left',
    color: 'rgb(var(--color-error))',
    className:
      'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
  },
  'contact-swap': {
    variant: 'ghost',
    color: 'var(--color-1-nuance-1)',
    icon: ArrowLeftRight,
    iconOnly: true,
    tooltipText: 'Échanger contact principal et secondaire',
  },
};

/** Props for {@link Button}. */
interface CustomButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'cancel'
    | 'danger'
    | 'ghost'
    | 'outline'
    | 'delete'
    | 'custom';
  /** Dynamic color for ghost/outline (same as {@link LabelBadge}). E.g. "var(--color-3-nuance-1)" or "#7c3aed". */
  color?: string;
  size?: 'sm' | 'md' | 'lg' | 'icon';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  preset?: ButtonPreset;
  tooltipText?: string;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  showBadge?: boolean;
  active?: boolean;
}

/** Tailwind classes for a variant; ghost/outline take an optional CSS color. */
const getVariantClasses = (variant: CustomButtonProps['variant'], color?: string) => {
  const variantMap = {
    primary: cn(
      'bg-[var(--color-2-nuance-1)] text-white shadow-md',
      'enabled:hover:bg-[var(--color-2-nuance-2)] enabled:hover:shadow-lg enabled:active:bg-[var(--color-2-nuance-1)]/90',
      'transition-all duration-200',
    ),
    secondary: cn(
      'bg-[var(--color-1-nuance-1)] text-white',
      'enabled:hover:bg-[var(--color-1-nuance-2)] enabled:hover:shadow-md enabled:active:bg-[var(--color-1-nuance-1)]/90',
      'transition-all duration-200',
    ),
    cancel: cn(
      'bg-[color-mix(in_srgb,white_99%,#e6e5e3_1%)] text-zinc-700 border border-zinc-400/85',
      'enabled:hover:bg-[color-mix(in_srgb,white_96%,#e0dfdc_4%)] enabled:hover:border-zinc-500 enabled:hover:text-zinc-800',
      'dark:bg-zinc-950 dark:text-zinc-200 dark:border-zinc-600',
      'dark:enabled:hover:bg-zinc-900 dark:enabled:hover:border-zinc-500 dark:enabled:hover:text-zinc-100',
      'transition-all duration-200',
    ),
    danger: cn(
      'bg-destructive text-destructive-foreground',
      'enabled:hover:bg-destructive/90 enabled:active:bg-destructive/80',
      'transition-colors duration-200',
    ),
    ghost: color
      ? cn(
          'bg-[color-mix(in_srgb,var(--btn-color)_15%,transparent)]',
          'text-[var(--btn-color)]',
          'border border-[color-mix(in_srgb,var(--btn-color)_30%,transparent)]',
          'enabled:hover:bg-[color-mix(in_srgb,var(--btn-color)_24%,transparent)]',
          'enabled:hover:border-[color-mix(in_srgb,var(--btn-color)_50%,transparent)]',
          'enabled:hover:text-[var(--btn-color)]',
          'transition-all duration-200',
        )
      : cn(
          'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_8%,transparent)]',
          'text-[var(--color-1-nuance-1)]',
          'border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)]',
          'enabled:hover:bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)]',
          'enabled:hover:border-[color-mix(in_srgb,var(--color-2-nuance-1)_50%,transparent)]',
          'enabled:hover:text-[var(--color-2-nuance-1)]',
          'transition-all duration-200',
        ),
    delete: cn(
      'bg-[color-mix(in_srgb,rgb(var(--color-error))_10%,transparent)]',
      'text-[rgb(var(--color-error))]',
      'border border-[rgb(var(--color-error))]',
      'enabled:hover:bg-[color-mix(in_srgb,rgb(var(--color-error))_15%,transparent)]',
      'enabled:hover:border-[rgb(var(--color-error))]',
      'enabled:hover:text-[rgb(var(--color-error))]',
      'transition-all duration-200',
    ),
    outline: color
      ? cn(
          'border border-[var(--btn-color)]',
          'bg-background',
          'text-[var(--btn-color)]',
          'enabled:hover:bg-[var(--btn-color)]',
          'enabled:hover:text-background',
          'shadow-sm enabled:hover:shadow-md',
          'transition-all duration-200',
        )
      : cn(
          'border border-[var(--color-2-nuance-1)]',
          'bg-background',
          'text-[var(--color-2-nuance-1)]',
          'enabled:hover:bg-[var(--color-2-nuance-1)]',
          'enabled:hover:text-background',
          'shadow-sm enabled:hover:shadow-md',
          'disabled:!bg-background disabled:!border-[color-mix(in_srgb,var(--color-2-nuance-1)_40%,transparent)] disabled:!text-[color-mix(in_srgb,var(--color-2-nuance-1)_40%,transparent)] disabled:!opacity-100',
          'disabled:!cursor-not-allowed disabled:shadow-none',
          'transition-all duration-200',
        ),
    custom: '',
  };

  return variantMap[variant ?? 'primary'];
};

/** Resolved button size after dropping the optional prop. */
type ButtonSize = NonNullable<CustomButtonProps['size']>;
/** Resolved button variant after dropping the optional prop. */
type ButtonVariant = NonNullable<CustomButtonProps['variant']>;

/** Height and padding for labeled buttons. */
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs rounded-lg',
  md: 'h-10 px-4 text-sm rounded-lg',
  lg: 'h-12 px-6 text-base rounded-xl',
  icon: 'h-8 px-3 text-xs rounded-lg',
};

/** Square hit area when the control is icon-only. */
const ICON_BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 w-8 p-0 rounded-lg',
  md: 'h-9 w-9 p-0 rounded-lg',
  lg: 'h-10 w-10 p-0 rounded-xl',
  icon: 'h-8 w-8 p-0 rounded-lg',
};

/** Lucide icon size per button size. */
const ICON_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  icon: 'h-4 w-4',
};

/** First value that is neither null nor undefined, otherwise the fallback. */
function coalesce<T>(fallback: T, ...values: Array<T | null | undefined>): T {
  for (const value of values) {
    if (value != null) return value;
  }
  return fallback;
}

/** Visual state after merging explicit props over the preset. */
interface ResolvedButton {
  variant: ButtonVariant;
  color?: string;
  size: ButtonSize;
  Icon?: LucideIcon;
  iconPosition: 'left' | 'right';
  tooltipText?: string;
  presetClassName: string;
  isIconButton: boolean;
  isFilterYesNoPreset: boolean;
}

/** Explicit props win over the preset. pagination-page variant follows `active`. */
function resolveButtonState(input: {
  preset?: ButtonPreset;
  variant?: CustomButtonProps['variant'];
  color?: string;
  size?: CustomButtonProps['size'];
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
  tooltipText?: string;
  children?: React.ReactNode;
  active?: boolean;
}): ResolvedButton {
  const presetConfig = input.preset ? BUTTON_PRESETS[input.preset] : {};
  const isPaginationPagePreset = input.preset === 'pagination-page' && input.active !== undefined;
  const isFilterYesNoPreset = input.preset === 'filter-yes' || input.preset === 'filter-no';
  const paginationVariant = input.active ? 'primary' : 'ghost';
  const resolvedPresetVariant = isPaginationPagePreset ? paginationVariant : presetConfig.variant;
  const size = coalesce<ButtonSize>('md', input.size, presetConfig.size);
  const Icon = input.icon ?? presetConfig.icon;
  const iconOnly = coalesce(false, input.iconOnly, presetConfig.iconOnly);

  return {
    variant: coalesce<ButtonVariant>('primary', input.variant, resolvedPresetVariant),
    color: input.color ?? presetConfig.color,
    size,
    Icon,
    iconPosition: coalesce<'left' | 'right'>('left', input.iconPosition, presetConfig.iconPosition),
    tooltipText: input.tooltipText ?? presetConfig.tooltipText,
    presetClassName: presetConfig.className ?? '',
    isIconButton: Boolean(iconOnly || size === 'icon' || (Icon && !input.children)),
    isFilterYesNoPreset,
  };
}

/** Compose variant, size, preset, and filter-active classes. */
function buildButtonClassName(input: {
  variant: ButtonVariant;
  color?: string;
  size: ButtonSize;
  isIconButton: boolean;
  fullWidth: boolean;
  showBadge: boolean;
  presetClassName: string;
  isFilterYesNoPreset: boolean;
  active?: boolean;
  className?: string;
}): string {
  const filterClass = input.active ? 'filter-badge-active' : 'filter-badge-inactive';
  return cn(
    'inline-flex items-center justify-center shrink-0 cursor-pointer disabled:cursor-not-allowed',
    getVariantClasses(input.variant, input.color),
    input.isIconButton ? ICON_BUTTON_SIZE_CLASSES[input.size] : SIZE_CLASSES[input.size],
    input.fullWidth && !input.isIconButton && 'w-full',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2',
    input.showBadge && 'relative',
    input.presetClassName,
    input.isFilterYesNoPreset && filterClass,
    input.className,
  );
}

/** CSS variables for tinted variants (`--btn-color`, optional `--filter-color`). */
function buildButtonStyle(
  color: string | undefined,
  isFilterYesNoPreset: boolean,
  styleProp?: React.CSSProperties,
): React.CSSProperties {
  if (!color) return styleProp ?? {};
  return {
    '--btn-color': color,
    ...(isFilterYesNoPreset ? { '--filter-color': color } : {}),
    ...styleProp,
  } as React.CSSProperties;
}

/** Render a Lucide icon when both the slot flag and the icon exist. */
function renderSlotIcon(
  show: unknown,
  Icon: LucideIcon | undefined,
  className: string,
): React.ReactNode {
  if (!show || !Icon) return null;
  return <Icon className={className} />;
}

/** Loader, icons, children, and optional unread badge inside the button. */
function renderButtonInner(input: {
  loading: boolean;
  Icon?: LucideIcon;
  isIconButton: boolean;
  iconPosition: 'left' | 'right';
  size: ButtonSize;
  showBadge: boolean;
  children?: React.ReactNode;
}): React.ReactNode {
  const { loading, Icon, isIconButton, iconPosition, size, showBadge, children } = input;
  const iconClass = ICON_SIZE_CLASSES[size];
  const showIconOnly = !loading && Icon && isIconButton;
  const showLeftIcon = !loading && Icon && !isIconButton && iconPosition === 'left';
  const showRightIcon = !loading && Icon && !isIconButton && iconPosition === 'right';

  return (
    <>
      {loading && (
        <Loader2 className={cn('animate-spin', iconClass, !isIconButton && children && 'mr-2')} />
      )}
      {renderSlotIcon(showIconOnly, Icon, iconClass)}
      {renderSlotIcon(showLeftIcon, Icon, cn(iconClass, children && 'mr-2'))}
      {!isIconButton && children}
      {renderSlotIcon(showRightIcon, Icon, cn(iconClass, children && 'ml-2'))}
      {showBadge && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[var(--color-2-nuance-1)]" />
      )}
    </>
  );
}

/** Form button with variants, presets, and optional loading/icon slots. */
export const Button = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      preset,
      variant: variantProp,
      color: colorProp,
      size: sizeProp,
      icon: iconProp,
      iconPosition: iconPositionProp,
      iconOnly: iconOnlyProp,
      loading = false,
      fullWidth = false,
      children,
      className: classNameProp,
      disabled,
      tooltipText: tooltipTextProp,
      tooltipSide = 'top',
      showBadge = false,
      active,
      style: styleProp,
      ...props
    },
    ref,
  ) => {
    const resolved = resolveButtonState({
      preset,
      variant: variantProp,
      color: colorProp,
      size: sizeProp,
      icon: iconProp,
      iconPosition: iconPositionProp,
      iconOnly: iconOnlyProp,
      tooltipText: tooltipTextProp,
      children,
      active,
    });

    const buttonClassName = buildButtonClassName({
      variant: resolved.variant,
      color: resolved.color,
      size: resolved.size,
      isIconButton: resolved.isIconButton,
      fullWidth,
      showBadge,
      presetClassName: resolved.presetClassName,
      isFilterYesNoPreset: resolved.isFilterYesNoPreset,
      active,
      className: classNameProp,
    });
    const buttonStyle = buildButtonStyle(resolved.color, resolved.isFilterYesNoPreset, styleProp);
    const buttonInner = renderButtonInner({
      loading,
      Icon: resolved.Icon,
      isIconButton: resolved.isIconButton,
      iconPosition: resolved.iconPosition,
      size: resolved.size,
      showBadge,
      children,
    });

    const buttonContent = (
      <ShadcnButton
        ref={ref}
        className={buttonClassName}
        disabled={disabled || loading}
        style={buttonStyle}
        {...props}
      >
        {buttonInner}
      </ShadcnButton>
    );

    if (!resolved.tooltipText) return buttonContent;

    // Tooltip cannot attach to a native disabled button. The outer control
    // stays enabled for the tooltip; the inner span is the visual and ignores
    // pointer events so hover styles never fire.
    const triggerContent = disabled ? (
      <button
        ref={ref}
        type="button"
        className="inline-flex shrink-0 border-0 bg-transparent p-0 cursor-not-allowed"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <span
          className={cn(buttonClassName, 'pointer-events-none opacity-50')}
          style={buttonStyle}
          aria-disabled="true"
        >
          {buttonInner}
        </span>
      </button>
    ) : (
      buttonContent
    );

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
          <TooltipContent side={tooltipSide}>
            <p>{resolved.tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

Button.displayName = 'Button';
